import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './context/AuthContext';
import DeathPoolForm from './components/DeathPoolForm';
import ViewSubmissions from './components/ViewSubmissions';
import LoginForm from './components/Auth/LoginForm';
import SignupForm from './components/Auth/SignupForm';
import ForgotPasswordForm from './components/Auth/ForgotPasswordForm';
import Help from './components/Help';
import './App.css';
import config from './config';

const GAME_START_DATE = new Date('2025-01-01T00:00:00');

function App() {
  const [currentView, setCurrentView] = useState('home');
  const [authView, setAuthView] = useState('login');
  const { user, token, logout } = useAuth();
  const [userList, setUserList] = useState(null);
  const [countdown, setCountdown] = useState('');
  const [isGameLocked, setIsGameLocked] = useState(false);

  useEffect(() => {
    // Check if game is locked
    const now = new Date();
    setIsGameLocked(now >= GAME_START_DATE);

    // Update countdown
    const updateCountdown = () => {
      const now = new Date();
      const diff = GAME_START_DATE - now;

      if (diff <= 0) {
        setCountdown('Game has started - lists are locked');
        setIsGameLocked(true);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      setCountdown(`Time until lists lock:\n${days} days, ${hours} hours, ${minutes} minutes`);
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (user && token) {
      loadUserList();
    }
  }, [user, token, currentView]);

  const loadUserList = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/my-list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUserList(response.data);
    } catch (error) {
      console.error('Error loading user list:', error);
    }
  };

  const handleAuthViewToggle = (view) => {
    setAuthView(view);
  };

  const renderAuthContent = () => {
    return (
      <div className="auth-container">
        <div className="welcome-message">
          <h2>Welcome to the Death List App</h2>
          <p>
            I made an app for our celebrity death list for 2025. No I'm not sure why either. You'll need to create an account or log in to get started.<br/><br/>
            Let me know if you have any trouble logging in or you find a bug<br/>
          </p>
        </div>
        {authView === 'signup' ? (
          <SignupForm onToggleForm={handleAuthViewToggle} />
        ) : authView === 'forgot' ? (
          <ForgotPasswordForm onToggleForm={handleAuthViewToggle} />
        ) : (
          <LoginForm onToggleForm={handleAuthViewToggle} />
        )}
      </div>
    );
  };

  const handleLogout = () => {
    logout();
    setAuthView('login');  // Reset to login view when logging out
  };

  const renderContent = () => {
    if (!user) {
      return renderAuthContent();
    }

    switch (currentView) {
      case 'manage':
        return isGameLocked ? (
          <div className="locked-message">
            <h2>Lists are now locked</h2>
            <p>The game started on January 1st, 2025. Lists can no longer be modified.</p>
            <button className="menu-button" onClick={() => setCurrentView('home')}>Back to Home</button>
          </div>
        ) : (
          <DeathPoolForm onBack={() => setCurrentView('home')} user={user} />
        );
      case 'view':
        return <ViewSubmissions onBack={() => setCurrentView('home')} />;
      case 'help':
        return <Help onBack={() => setCurrentView('home')} />;
      default:
        return (
          <div className="home-options">
            <div className="countdown-timer">{countdown}</div>
            <button onClick={() => setCurrentView('view')}>View Everyone's Lists</button>
            {!isGameLocked && (
              <button onClick={() => setCurrentView('manage')}>
                {(!userList || !userList.celebrities || userList.celebrities.length === 0)
                  ? "Start Your List Here"
                  : "Manage Your List"}
              </button>
            )}
            <button onClick={() => setCurrentView('help')} className="help-button">
              Help
            </button>
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </div>
        );
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Celebrity Death List 2025</h1>
        {user && (
          <div className="user-info">
            <b>Welcome, {user.name}</b>
          </div>
        )}
      </header>
      <main>
        {renderContent()}
      </main>
    </div>
  );
}

export default App; 